import "react-international-phone/style.css";
import "./phoneInput.scss";

import React from "react";
import { Form, FormItemProps } from "antd";
import type { Rule } from "antd/es/form";
import classNames from "clsx";
import {
  PhoneInput as InternationalPhoneInput,
} from "react-international-phone";

import { SizeEnum } from "@/types";
import { REQUIRED_VALIDATION_MESSAGE } from "@/utils/constant";

/**
 * Formats phone number to ensure hyphen between country code and number
 * Uses country dial code to properly split the number
 * Example: +449999999999 with dialCode "44" -> +44-9999999999
 */
const formatPhoneWithHyphen = (phone: string, dialCode: string): string => {
  if (!phone || typeof phone !== 'string') return phone;

  // Remove any existing spaces, hyphens, or parentheses
  const cleaned = phone.replace(/[\s\-()]/g, '');

  // If phone starts with +{dialCode}, split it properly
  if (dialCode && cleaned.startsWith(`+${dialCode}`)) {
    const nationalNumber = cleaned.substring(dialCode.length + 1); // +1 for the "+"
    return `+${dialCode}-${nationalNumber}`;
  }

  // Fallback: try to match pattern
  const match = cleaned.match(/^\+(\d{1,3})(\d+)$/);
  if (match) {
    return `+${match[1]}-${match[2]}`;
  }

  return phone;
};

interface PhoneInputProps extends FormItemProps {
  name: string;
  label?: string;
  placeholder?: string;
  size?: SizeEnum;
  required?: boolean;
  disabled?: boolean;
  isSeperator?: boolean;
  maxLength?: number;
  onChangeListener?: (phone: string) => void;
}

export const PhoneInput = ({
  name,
  label,
  placeholder = "Enter phone number",
  size = SizeEnum.large,
  required = false,
  isSeperator = false,
  maxLength = 15,
  onChangeListener,
  rules = [],
  ...rest
}: PhoneInputProps) => {
  const [currentDialCode, setCurrentDialCode] = React.useState<string>("44");

  const getRules = (): Rule[] => {
    return [
      ...rules,
      { required, message: REQUIRED_VALIDATION_MESSAGE },
      { max: maxLength, message: `Phone number must be at most ${maxLength} characters` },
    ];
  };

  return (
    <div
      className={classNames(
        "custom_form_comp_wrapper",
        rest?.hidden && "h-0 overflow-hidden",
        isSeperator && "flex items-center gap-x-2"
      )}
    >
      {label && (
        <label
          htmlFor={name}
          className={classNames(
            "block text-primary-0",
            isSeperator ? "mb-6" : "mb-2"
          )}
        >
          {label}
        </label>
      )}
      {isSeperator && (
        <div className="mb-6 flex-[1] basis-40 border border-dashed border-disabled" />
      )}
      <div className={classNames(isSeperator && "basis-[288px]")}>
        <Form.Item
          name={name}
          rules={getRules()}
          getValueFromEvent={(phone: string) => formatPhoneWithHyphen(phone, currentDialCode)}
          {...rest}
        >
          <InternationalPhoneInput
            defaultCountry="gb"
            placeholder={placeholder}
            disabled={rest?.disabled}
            onChange={(phone, meta) => {
              // Update current dial code when country changes
              if (meta?.country?.dialCode) {
                setCurrentDialCode(meta.country.dialCode);
              }
              const formattedPhone = formatPhoneWithHyphen(phone, meta?.country?.dialCode || currentDialCode);
              onChangeListener?.(formattedPhone);
            }}
            forceDialCode={true}
            inputProps={{ maxLength }}
            inputClassName="phone-input-field placeholder:text-sm placeholder:text-[#62748E80]/80 border-none bg-[#F8FAFC] h-[44px] w-full"
            className="h-[44px]"
            countrySelectorStyleProps={{
              buttonClassName: "phone-country-selector",
              dropdownStyleProps: {
                className: "phone-country-dropdown",
              },
            }}
            style={{
              width: "100%",
              height: size === SizeEnum.large ? "40px" : "32px",
            }}
          />
        </Form.Item>
      </div>
    </div>
  );
};
